/**
 * Утилиты для работы с согласиями на обработку персональных данных
 */

import { ConsentVersions } from '../components/ConsentModal/types';

const CONSENT_VERSIONS_KEY = 'consent_versions';
const CONSENT_GRANTED_DATE_KEY = 'consent_granted_date';

// Типы согласий, которые требуются для работы с системой
export const REQUIRED_CONSENT_TYPES = [
    'data_processing_consent',
    'data_transfer_consent',
    'data_processing_policy_consent'
] as const;

/**
 * Получает сохраненные версии согласий из localStorage
 */
export const getConsentVersions = (): ConsentVersions => {
    const versionsStr = localStorage.getItem(CONSENT_VERSIONS_KEY);
    if (!versionsStr) {
        return {};
    }
    try {
        return JSON.parse(versionsStr);
    } catch (error) {
        console.error('Error parsing consent versions:', error);
        return {};
    }
};

/**
 * Сохраняет версии согласий в localStorage
 */
export const setConsentVersions = (versions: ConsentVersions): void => {
    localStorage.setItem(CONSENT_VERSIONS_KEY, JSON.stringify(versions));
    localStorage.setItem(CONSENT_GRANTED_DATE_KEY, new Date().toISOString());
};

/**
 * Обновляет версию конкретного типа согласия
 */
export const updateConsentVersion = (consentType: keyof ConsentVersions, version: number): void => {
    const currentVersions = getConsentVersions();
    currentVersions[consentType] = version;
    setConsentVersions(currentVersions);
};

/**
 * Проверяет, нужно ли показывать модальное окно согласий
 * Сравнивает версии из API с сохраненными версиями
 */
export const shouldShowConsentModal = (
    serverVersions: ConsentVersions
): boolean => {
    const localVersions = getConsentVersions();
    
    // Проверяем каждый обязательный тип согласия
    for (const consentType of REQUIRED_CONSENT_TYPES) {
        const serverVersion = serverVersions[consentType];
        const localVersion = localVersions[consentType];
        
        // Если нет локальной версии или серверная версия выше
        if (!localVersion || (serverVersion && serverVersion > localVersion)) {
            return true;
        }
    }
    
    return false;
};

/**
 * Получает дату предоставления согласия
 */
export const getConsentGrantedDate = (): Date | null => {
    const dateStr = localStorage.getItem(CONSENT_GRANTED_DATE_KEY);
    return dateStr ? new Date(dateStr) : null;
};

/**
 * Сбрасывает информацию о согласии (для тестирования или повторного запроса)
 */
export const clearConsent = (): void => {
    localStorage.removeItem(CONSENT_VERSIONS_KEY);
    localStorage.removeItem(CONSENT_GRANTED_DATE_KEY);
};

/**
 * DEPRECATED: Оставлено для обратной совместимости
 * Используйте getConsentVersions() и shouldShowConsentModal() вместо этого
 */
export const isConsentGranted = (): boolean => {
    const versions = getConsentVersions();
    // Проверяем, что все обязательные согласия имеют версии
    return REQUIRED_CONSENT_TYPES.every(type => versions[type] !== undefined);
};

/**
 * DEPRECATED: Оставлено для обратной совместимости
 * Используйте setConsentVersions() вместо этого
 */
export const setConsentGranted = (): void => {
    // Эта функция устарела, но оставлена для обратной совместимости
    console.warn('setConsentGranted() is deprecated. Use setConsentVersions() instead.');
};

